import { Injectable } from '@angular/core';
import { isLoopbackInputLabel, MicrophoneCheckResult, MicrophoneLevel } from 'src/app/models/microphone-check.model';

/**
 * Peak below which no signal was captured at all, in the -1..1 time domain.
 *
 * Not a taste judgement about loudness: 1/32768 is one least-significant bit of the 16-bit samples
 * the device delivers, so a peak under it cannot represent any captured sound — it is the absence of
 * audio rather than quiet audio. That makes it the only level that can be refused with certainty,
 * which is why it is the one that blocks a call.
 */
const DEAD_PEAK_THRESHOLD = 1 / 32768;

/**
 * RMS below which the input is suspiciously quiet — reported, never blocking.
 *
 * -60 dBFS sits about 100x under a healthy channel (measured around -27 dBFS on this platform's own
 * recordings) and above every level measured on a failing one. It is deliberately NOT a gate: Chrome
 * applies noise suppression and automatic gain control by default, so a working microphone in a
 * silent room can legitimately read very low, and refusing those calls would trade an intermittent
 * "the patient cannot hear me" for a hard "I cannot call". Until the band is calibrated against real
 * hardware, everything that lands here is reported with its measured level so the threshold can be
 * set from data instead of from judgement.
 */
const SUSPECT_RMS_THRESHOLD = 1e-3;

/** How long to listen. Short enough to be invisible on a click, long enough for a stable RMS. */
const DEFAULT_SAMPLE_MS = 300;

/** Gap between reads while listening. */
const SAMPLE_INTERVAL_MS = 25;

/** Floor for the dBFS conversion, so a digitally silent track reports a number rather than -Infinity. */
const MIN_DBFS = -120;

/**
 * Verifies that a microphone track is actually capturing, independently of the Voice SDK.
 *
 * Kept apart from {@link TwilioService} because it touches no Twilio API: it takes the stream the
 * SDK will send and answers whether audio is coming out of it. That makes it testable with a plain
 * fake stream, and reusable for any other capture the platform adds later.
 *
 * Measures both peak and RMS on purpose. Peak answers "was anything captured at all" and is immune
 * to how loud the room is; RMS answers "how much", and is the number worth calibrating a threshold
 * against, because a single click or a DC offset can lift a peak off the floor while the channel
 * still carries nothing a patient could hear.
 */
@Injectable({
  providedIn: 'root',
})
export class MicrophoneCheckService {
  /** Level in decibels relative to full scale, floored so digital silence is still a number. */
  static toDbfs(amplitude: number): number {
    return amplitude > 0 ? Math.max(MIN_DBFS, Math.round(20 * Math.log10(amplitude) * 10) / 10) : MIN_DBFS;
  }

  /**
   * Inspects the first audio track of `stream` and measures its real input level.
   *
   * Three outcomes matter to the caller: `silent` (nothing was captured — refuse the call),
   * `very-quiet` (captured, but far below a healthy channel — let the call through and report it),
   * and `ok`.
   */
  async verifyStream(stream: MediaStream | null, sampleMs: number = DEFAULT_SAMPLE_MS): Promise<MicrophoneCheckResult> {
    const track = stream?.getAudioTracks?.()[0];
    if (!track) {
      return { status: 'no-device' };
    }

    const identity = { label: track.label || undefined, deviceId: track.getSettings?.().deviceId };

    if (track.readyState !== 'live') {
      return { status: 'track-ended', ...identity };
    }
    // A track the OS or browser is holding muted produces zeroes and recovers only on re-acquisition.
    if (track.muted) {
      return { status: 'track-muted', ...identity };
    }
    // Before the level: a loopback input reads as merely quiet while nothing plays, which would pass.
    if (isLoopbackInputLabel(track.label)) {
      return { status: 'loopback-device', ...identity };
    }

    const level = await this.measureLevel(stream, sampleMs);
    if (!level) {
      // Web Audio missing: the track's own flags are all the evidence available, and they were fine.
      return { status: 'unsupported', ...identity };
    }

    if (level.peak < DEAD_PEAK_THRESHOLD) {
      return { status: 'silent', ...level, ...identity };
    }
    if (level.rms < SUSPECT_RMS_THRESHOLD) {
      return { status: 'very-quiet', ...level, ...identity };
    }

    return { status: 'ok', ...level, ...identity };
  }

  /**
   * Peak and RMS heard on the stream, or null when Web Audio is unavailable.
   *
   * The whole window is always listened to — there is no early exit on the first sample above the
   * floor, because RMS over a partial window would be the thing the threshold is calibrated against.
   * The AudioContext is created per check and closed straight after: one left open holds the device
   * awake and counts against the browser's per-page context limit, and this runs before every call.
   */
  private async measureLevel(stream: MediaStream, sampleMs: number): Promise<MicrophoneLevel | null> {
    const audioContext = this.createAudioContext();
    if (!audioContext) {
      return null;
    }

    let source: MediaStreamAudioSourceNode | null = null;
    try {
      // The check runs from the click that places the call, so the context is allowed to start; a
      // context created while the page was still gestureless stays suspended and reads only zeroes.
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      const buffer = new Float32Array(analyser.fftSize);
      // The analyser holds fftSize samples, which at 48 kHz is 42.7 ms — longer than the read
      // interval, so consecutive reads overlap and nothing is missed. On a 96 kHz interface that
      // window shrinks to 21.3 ms and a fixed 25 ms interval would start skipping audio between
      // reads, which for a level measurement means silence that was never actually there.
      const windowMs = (analyser.fftSize / audioContext.sampleRate) * 1000;
      const intervalMs = Math.max(1, Math.min(SAMPLE_INTERVAL_MS, Math.floor(windowMs)));
      const deadline = Date.now() + sampleMs;
      let peak = 0;
      let sumOfSquares = 0;
      let sampleCount = 0;

      while (Date.now() < deadline) {
        analyser.getFloatTimeDomainData(buffer);
        for (const sample of buffer) {
          const magnitude = Math.abs(sample);
          if (magnitude > peak) {
            peak = magnitude;
          }
          sumOfSquares += sample * sample;
        }
        sampleCount += buffer.length;
        await this.wait(intervalMs);
      }

      const rms = sampleCount ? Math.sqrt(sumOfSquares / sampleCount) : 0;
      return { peak, rms, peakDbfs: MicrophoneCheckService.toDbfs(peak), rmsDbfs: MicrophoneCheckService.toDbfs(rms) };
    } catch (error) {
      console.error('[Microphone] Unable to measure input level', error);
      return null;
    } finally {
      source?.disconnect();
      await audioContext.close().catch(() => undefined);
    }
  }

  /** Isolated so tests can stub Web Audio, and so an unsupported browser degrades instead of throwing. */
  private createAudioContext(): AudioContext | null {
    const ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    return ctor ? new ctor() : null;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
